import React, { useEffect, useMemo, useRef, useState } from 'react'

import PropTypes from 'prop-types'

import { ARCHETYPE_CATEGORY_PRIORITIES } from '../../../utils/Constants'
import Permissions, { CAPABILITIES } from '../../../utils/Permissions'
import MagicWand from '../../common/MagicWand'

import EntityCard from './EntityCard'

export const CATEGORIES = [
  { key: 'all', label: 'All Entities' },
  { key: 'provider', label: 'Treatment Providers' },
  { key: 'diagnosis', label: 'Diagnoses' },
  { key: 'test', label: 'Tests & Imaging' },
  { key: 'medication', label: 'Medications & Allergies' },
  { key: 'appointment', label: 'Appointments & Referrals' },
  { key: 'status', label: 'Status & Surgeries' },
  { key: 'referral', label: 'Case & Demographics' },
  { key: 'task', label: 'Proposed Tasks & Follow-ups' },
  { key: 'contact', label: 'Key Contacts' },
]

export function matchesCategory(entityCategoryOrEntity, targetCategory) {
  const entityCategory =
    typeof entityCategoryOrEntity === 'object' && entityCategoryOrEntity !== null
      ? entityCategoryOrEntity.category
      : entityCategoryOrEntity
  if (targetCategory === 'all') return true
  if (targetCategory === 'medication') {
    return entityCategory === 'medication' || entityCategory === 'allergy'
  }
  if (targetCategory === 'status') {
    return (
      entityCategory === 'status' ||
      entityCategory === 'surgery' ||
      entityCategory === 'mmi' ||
      entityCategory === 'workStatus'
    )
  }
  if (targetCategory === 'appointment') {
    return entityCategory === 'appointment' || entityCategory === 'referral_appt'
  }
  if (targetCategory === 'referral') {
    return entityCategory === 'referral' || entityCategory === 'demographics' || entityCategory === 'insured'
  }
  if (targetCategory === 'task') {
    return entityCategory === 'task' || entityCategory === 'activity'
  }
  if (targetCategory === 'contact') {
    return entityCategory === 'contact' || entityCategory === 'keyContact' || entityCategory === 'keycontact'
  }
  return entityCategory === targetCategory
}

export default function HitlReviewDrawer({
  title = 'Smart Upload',
  documentSubtype = null,
  entities = [],
  activeEntityId,
  onSelectEntity,
  onAcceptEntity,
  onRejectEntity,
  onMergeEntity,
  onAssociateOrganization,
  onEditEntity,
  onUndoEntity,
  onUpdateMedicationFlags,
  onAcceptAllInCategory,
  onApproveAndApply,
  onClose,
  onCancelAndDiscard,
  availableOrganizations = [],
  user,
  isApplying = false,
  isAnalyzing = false,
  readOnly = false,
}) {
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [logRecordReviewTime, setLogRecordReviewTime] = useState(true)
  const [recordReviewHours, setRecordReviewHours] = useState(0.2)
  const cardRefs = useRef({})
  const scrollAreaRef = useRef(null)
  const prevActiveEntityIdRef = useRef(activeEntityId)

  // Auto-scroll to active card when activeEntityId changes (e.g. clicking a highlight in the document viewer)
  useEffect(() => {
    const activeIdChanged = prevActiveEntityIdRef.current !== activeEntityId
    prevActiveEntityIdRef.current = activeEntityId

    if (!activeEntityId) return
    if (activeIdChanged) {
      const activeEnt = entities.find(e => e.id === activeEntityId)
      if (activeEnt && selectedCategory !== 'all' && !matchesCategory(activeEnt.category, selectedCategory)) {
        setSelectedCategory('all')
      }
    }
    const timer = setTimeout(() => {
      if (cardRefs.current[activeEntityId] && typeof cardRefs.current[activeEntityId].scrollIntoView === 'function') {
        cardRefs.current[activeEntityId].scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }, 60)
    return () => clearTimeout(timer)
  }, [activeEntityId, entities, selectedCategory])

  useEffect(() => {
    if (!isAnalyzing) {
      setElapsedSeconds(0)
      return
    }
    const interval = setInterval(() => {
      setElapsedSeconds(s => s + 1)
    }, 1000)
    return () => clearInterval(interval)
  }, [isAnalyzing])

  const orderedCategories = useMemo(() => {
    if (!documentSubtype || !ARCHETYPE_CATEGORY_PRIORITIES[documentSubtype]) {
      return CATEGORIES
    }
    const priorityList = ARCHETYPE_CATEGORY_PRIORITIES[documentSubtype]
    const getPriority = key => {
      if (key === 'all') return -1
      if (key === 'status') {
        const idxStatus = priorityList.indexOf('status')
        const idxSurgery = priorityList.indexOf('surgery')
        return Math.min(idxStatus >= 0 ? idxStatus : 999, idxSurgery >= 0 ? idxSurgery : 999)
      }
      if (key === 'referral') {
        const idxRef = priorityList.indexOf('referral')
        const idxDemo = priorityList.indexOf('demographics')
        return Math.min(idxRef >= 0 ? idxRef : 999, idxDemo >= 0 ? idxDemo : 999)
      }
      const idx = priorityList.indexOf(key)
      return idx >= 0 ? idx : 999
    }

    return [...CATEGORIES].sort((a, b) => getPriority(a.key) - getPriority(b.key))
  }, [documentSubtype])

  const canApply = Permissions.can(user, CAPABILITIES.UPDATE_CASE) || Permissions.can(user, CAPABILITIES.CREATE_CASE)

  // Reviewable candidate entities: in consolidated clusters, only the leader (occurrenceIndex <= 1) is presented for review
  const reviewableEntities = useMemo(
    () => entities.filter(e => !e.occurrenceIndex || e.occurrenceIndex === 1),
    [entities],
  )

  // Filter entities by category
  const filteredEntities = reviewableEntities.filter(e => matchesCategory(e.category, selectedCategory))

  // Stepper & Progress calculations on unique reviewable candidate cards
  const totalCount = reviewableEntities.length
  const acceptedCount = reviewableEntities.filter(e => e.status === 'accepted' || e.status === 'edited').length
  const mergedCount = reviewableEntities.filter(e => e.status === 'merged').length
  const skippedCount = reviewableEntities.filter(e => e.status === 'rejected').length
  const pendingCount = reviewableEntities.filter(e => !e.status || e.status === 'pending').length

  const resolvedCount = reviewableEntities.filter(
    e => e.status === 'accepted' || e.status === 'rejected' || e.status === 'merged' || e.status === 'edited',
  ).length
  const percentComplete = totalCount > 0 ? Math.round((resolvedCount / totalCount) * 100) : isAnalyzing ? 0 : 100

  // Active index within filtered list
  const activeIndex = filteredEntities.findIndex(e => e.id === activeEntityId)
  const currentActiveIndex = activeIndex >= 0 ? activeIndex : 0

  const handleStepJump = newIdx => {
    if (newIdx >= 0 && newIdx < filteredEntities.length) {
      onSelectEntity(filteredEntities[newIdx])
    }
  }

  const handleCategorySelect = catKey => {
    setSelectedCategory(catKey)
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = 0
    }
    const matching = reviewableEntities.filter(e => matchesCategory(e.category, catKey))
    if (matching.length > 0) {
      const activeStillInCategory = activeEntityId && matching.some(e => e.id === activeEntityId)
      if (!activeStillInCategory && onSelectEntity) {
        prevActiveEntityIdRef.current = matching[0].id
        onSelectEntity(matching[0])
      }
    }
  }

  const hasPendingInCategory = filteredEntities.some(e => !e.status || e.status === 'pending')

  return (
    <div className="hitl-right-pane hitl-drawer-container">
      {/* Header Branded with Smart Upload */}
      <div className="hitl-drawer-header bg-info text-white d-flex justify-content-between align-items-center p-3 rounded-top mb-3 shadow-sm">
        <div>
          <h5 className="mb-0 font-weight-bold d-flex align-items-center" style={{ fontSize: '1.05rem' }}>
            <span
              style={{
                width: '18px',
                height: '18px',
                display: 'inline-block',
                verticalAlign: 'middle',
                marginRight: '8px',
                flexShrink: 0,
              }}
            >
              <MagicWand active={true} />
            </span>
            {title}
          </h5>
          <small style={{ opacity: 0.9 }}>
            Review, reconcile, and confirm proposed clinical and case entities from intake documents
          </small>
        </div>
        <div className="d-flex align-items-center" style={{ gap: '6px' }}>
          {isAnalyzing && (
            <button
              type="button"
              className="btn btn-xs btn-outline-light font-weight-bold text-white"
              onClick={onClose}
              title="Let analysis finish in the background while you continue working"
              style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem', borderWidth: '1px' }}
            >
              <i className="fas fa-compress-alt mr-1" /> Run in Background
            </button>
          )}
          {(onCancelAndDiscard || onClose) && (
            <button
              type="button"
              className="btn btn-xs btn-outline-light font-weight-bold text-white"
              onClick={onClose}
              title={isAnalyzing ? 'Close modal (analysis continues in background)' : 'Close Review'}
              style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', borderWidth: '1px' }}
            >
              <i className="fas fa-times mr-1" /> Close
            </button>
          )}
        </div>
      </div>

      {/* Visual Progress Bar */}
      <div className="hitl-progress-wrapper mb-3">
        <div className="d-flex justify-content-between align-items-center mb-1 small text-muted font-weight-bold">
          <span>Confirmation Progress</span>
          <span>
            {isAnalyzing
              ? 'Analyzing documents...'
              : `${resolvedCount} of ${totalCount} (${percentComplete}%) Complete`}
          </span>
        </div>
        <div className="progress" style={{ height: '6px', borderRadius: '3px' }}>
          <div
            className={`progress-bar ${
              isAnalyzing
                ? 'progress-bar-striped progress-bar-animated bg-info'
                : percentComplete === 100
                ? 'bg-success'
                : 'bg-info'
            }`}
            role="progressbar"
            style={{ width: isAnalyzing ? '100%' : `${percentComplete}%`, transition: 'width 0.3s ease' }}
          />
        </div>
      </div>

      {/* Category Filter Tabs */}
      <div className="hitl-category-tabs mb-2">
        {orderedCategories.map(cat => {
          const count = reviewableEntities.filter(e => matchesCategory(e.category, cat.key)).length
          if (count === 0 && cat.key !== 'all' && !isAnalyzing) return null

          return (
            <button
              key={cat.key}
              type="button"
              className={`category-tab-btn ${selectedCategory === cat.key ? 'active font-weight-bold' : ''}`}
              onClick={() => handleCategorySelect(cat.key)}
            >
              {cat.label}
              <span
                className={`badge ml-1 ${selectedCategory === cat.key ? 'badge-light text-dark' : 'badge-secondary'}`}
              >
                {count}
              </span>
            </button>
          )
        })}
      </div>

      {/* Stepper Navigation Controls */}
      {filteredEntities.length > 0 && (
        <div className="hitl-stepper-control mb-3 p-2 bg-light border rounded">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="small font-weight-bold text-muted">Active Candidate Entity:</span>
            {hasPendingInCategory && !readOnly && !isAnalyzing && (
              <button
                type="button"
                className="btn btn-link btn-xs text-primary p-0 font-weight-bold"
                style={{ fontSize: '0.75rem', textDecoration: 'none' }}
                onClick={() => onAcceptAllInCategory && onAcceptAllInCategory(selectedCategory)}
              >
                Accept All Pending in Category
              </button>
            )}
          </div>

          <div className="d-flex align-items-center mb-1" style={{ gap: '6px' }}>
            <select
              className="form-control form-control-sm font-weight-bold flex-grow-1"
              value={currentActiveIndex}
              onChange={e => handleStepJump(parseInt(e.target.value, 10))}
              title={filteredEntities[currentActiveIndex]?.extractedValue || ''}
              style={{ fontSize: '0.8rem', border: '1px solid #17a2b8', maxWidth: '100%', minWidth: 0 }}
            >
              {filteredEntities.map((ent, idx) => {
                const rawVal = ent.extractedValue || ent.attributes?.practiceName || '(Pending)'
                const cleanVal = String(rawVal).replace(/\s+/g, ' ').trim()
                const truncatedVal = cleanVal.length > 45 ? `${cleanVal.slice(0, 45)}...` : cleanVal
                const occSuffix =
                  ent.totalOccurrences > 1 ? ` [Occ ${ent.occurrenceIndex}/${ent.totalOccurrences}]` : ''
                const statusSymbol =
                  ent.status === 'accepted' || ent.status === 'edited'
                    ? '✓ '
                    : ent.status === 'merged'
                    ? '⚡ '
                    : ent.status === 'rejected'
                    ? '✕ '
                    : '⚠️ '

                return (
                  <option key={ent.id} value={idx} title={cleanVal}>
                    {idx + 1}. {statusSymbol}
                    {ent.field}: {truncatedVal}
                    {occSuffix}
                  </option>
                )
              })}
            </select>

            {/* Stepper Jump Buttons */}
            <div className="d-flex" style={{ gap: '3px', flexShrink: 0 }}>
              <button
                type="button"
                className="btn btn-sm btn-outline-dark font-weight-bold"
                onClick={() => handleStepJump(0)}
                disabled={currentActiveIndex === 0}
                title="Go to First Entity (Top)"
                style={{ padding: '0.2rem 0.4rem', fontSize: '0.75rem' }}
              >
                ≪ Top
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-dark font-weight-bold"
                onClick={() => handleStepJump(currentActiveIndex - 1)}
                disabled={currentActiveIndex === 0}
                title="Previous Entity"
                style={{ padding: '0.2rem 0.45rem', fontSize: '0.75rem' }}
              >
                ◄ Prev
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-dark font-weight-bold"
                onClick={() => handleStepJump(currentActiveIndex + 1)}
                disabled={currentActiveIndex >= filteredEntities.length - 1}
                title="Next Entity"
                style={{ padding: '0.2rem 0.45rem', fontSize: '0.75rem' }}
              >
                Next ►
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-dark font-weight-bold"
                onClick={() => handleStepJump(filteredEntities.length - 1)}
                disabled={currentActiveIndex >= filteredEntities.length - 1}
                title="Go to Last Entity (Bottom)"
                style={{ padding: '0.2rem 0.4rem', fontSize: '0.75rem' }}
              >
                Bottom ≫
              </button>
            </div>
          </div>

          {/* Prompt Undo Action if Current Item is Accepted/Rejected/Merged */}
          {filteredEntities[currentActiveIndex] &&
            (filteredEntities[currentActiveIndex].status === 'accepted' ||
              filteredEntities[currentActiveIndex].status === 'rejected' ||
              filteredEntities[currentActiveIndex].status === 'merged') &&
            !readOnly && (
              <div className="d-flex justify-content-between align-items-center mt-2 pt-1 border-top">
                <span className="small text-muted font-italic">
                  Current entity decision:{' '}
                  <strong
                    className={
                      filteredEntities[currentActiveIndex].status === 'accepted' ? 'text-success' : 'text-secondary'
                    }
                  >
                    {filteredEntities[currentActiveIndex].status.toUpperCase()}
                  </strong>
                </span>
                <button
                  type="button"
                  className="btn btn-xs btn-outline-secondary font-weight-bold"
                  onClick={() => onUndoEntity && onUndoEntity(filteredEntities[currentActiveIndex].id)}
                  title="Undo decision and reset candidate back to Pending"
                  style={{ fontSize: '0.75rem', padding: '0.15rem 0.5rem' }}
                >
                  <i className="fas fa-undo mr-1" /> Undo Decision
                </button>
              </div>
            )}
        </div>
      )}

      {/* Scrollable List of Entity Cards */}
      <div ref={scrollAreaRef} className="hitl-cards-scroll-area flex-grow-1" style={{ overflowY: 'auto' }}>
        {isAnalyzing && filteredEntities.length === 0 ? (
          <div className="p-3">
            <div className="alert alert-info py-2 px-3 mb-3 small d-flex align-items-center">
              <div
                style={{
                  width: '26px',
                  height: '26px',
                  flexShrink: 0,
                  marginRight: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <MagicWand active={true} />
              </div>
              <div className="w-100">
                <div className="d-flex justify-content-between align-items-center">
                  <strong>GuideSmart AI is analyzing your documents...</strong>
                  <span className="badge badge-light text-info font-weight-bold ml-2">
                    <span
                      className="spinner-border spinner-border-sm mr-1 text-info"
                      role="status"
                      aria-hidden="true"
                      style={{ width: '9px', height: '9px', borderWidth: '1.5px' }}
                    />
                    Elapsed: {Math.floor(elapsedSeconds / 60)}m {String(elapsedSeconds % 60).padStart(2, '0')}s
                  </span>
                </div>
                <span className="text-muted d-block mt-1" style={{ fontSize: '0.78rem' }}>
                  {elapsedSeconds >= 90
                    ? 'Processing large document record. Comprehensive records with dozens or hundreds of pages (e.g. 30MB+ scans) may take 15–20 minutes. You can safely let this run in the background.'
                    : 'OCR text extraction and clinical reasoning are running in parallel. Large files or multi-document batches may take several minutes.'}
                </span>
              </div>
            </div>
            <div className="text-center mb-3">
              <button
                type="button"
                className="btn btn-sm btn-outline-primary font-weight-bold shadow-sm"
                onClick={onClose}
                title="Your documents are saved in the case. Extractions will be staged for review when ready."
              >
                <i className="fas fa-compress-alt mr-1" /> Continue Working in Background
              </button>
              <div className="small text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                Your documents are securely stored in the case. You can close this window at any time.
              </div>
            </div>
            {[1, 2, 3, 4].map(n => (
              <div key={n} className="hitl-skeleton-card mb-3">
                <div className="skeleton-line title w-50 mb-2" />
                <div className="skeleton-line w-75 mb-2" />
                <div className="skeleton-line w-25" />
              </div>
            ))}
          </div>
        ) : filteredEntities.length > 0 ? (
          filteredEntities.map(ent => (
            <div
              key={ent.id}
              ref={el => {
                cardRefs.current[ent.id] = el
              }}
              className={`hitl-card-wrapper ${ent.id === activeEntityId ? 'hitl-card-sync-active rounded' : ''}`}
            >
              <EntityCard
                entity={ent}
                isActive={ent.id === activeEntityId}
                onSelect={onSelectEntity}
                onAccept={onAcceptEntity}
                onReject={onRejectEntity}
                onMerge={onMergeEntity}
                onAssociateOrganization={onAssociateOrganization}
                onEdit={onEditEntity}
                onUndo={onUndoEntity}
                onUpdateMedicationFlags={onUpdateMedicationFlags}
                availableOrganizations={availableOrganizations}
                readOnly={readOnly}
              />
            </div>
          ))
        ) : (
          <div className="text-center text-muted my-4">
            <p>No entities found for this category.</p>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      {!readOnly && (
        <div className="pt-2 border-top mt-2">
          {!isAnalyzing && (!user || canApply) && (
            <div
              className="d-flex align-items-center justify-content-between mb-2 px-2 py-1 rounded border bg-light"
              data-testid="hitl-billable-time-bar"
              style={{ fontSize: '0.78rem' }}
            >
              <label
                className="d-flex align-items-center mb-0 text-dark font-weight-bold"
                style={{ cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  className="mr-2"
                  data-testid="hitl-log-time-toggle"
                  checked={logRecordReviewTime}
                  onChange={e => setLogRecordReviewTime(e.target.checked)}
                />
                <i className="fas fa-stopwatch text-primary mr-1" />
                <span>Log Record Review Time</span>
              </label>
              {logRecordReviewTime && (
                <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                  <button
                    type="button"
                    className="btn btn-xs btn-outline-secondary py-0 px-2"
                    data-testid="hitl-time-decrement"
                    onClick={() => setRecordReviewHours(prev => Math.max(0.1, Math.round((prev - 0.1) * 10) / 10))}
                  >
                    -
                  </button>
                  <span
                    className="font-weight-bold px-1 text-primary"
                    data-testid="hitl-time-hours"
                    style={{ minWidth: '34px', textAlign: 'center' }}
                  >
                    {Number(recordReviewHours).toFixed(1)}h
                  </span>
                  <button
                    type="button"
                    className="btn btn-xs btn-outline-secondary py-0 px-2"
                    data-testid="hitl-time-increment"
                    onClick={() => setRecordReviewHours(prev => Math.min(4.0, Math.round((prev + 0.1) * 10) / 10))}
                  >
                    +
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center">
            <div className="small text-muted d-flex flex-wrap" style={{ gap: '8px' }}>
              <span className="text-success font-weight-bold">
                <i className="fas fa-check mr-1" />
                {acceptedCount} Accepted
              </span>
              {mergedCount > 0 && (
                <span className="text-info font-weight-bold">
                  <i className="fas fa-bolt mr-1" />
                  {mergedCount} Merged
                </span>
              )}
              <span className="text-secondary font-weight-bold">
                <i className="fas fa-times mr-1" />
                {skippedCount} Skipped
              </span>
              {pendingCount > 0 && (
                <span className="text-warning font-weight-bold">
                  <i className="fas fa-exclamation-circle mr-1" />
                  {pendingCount} Pending
                </span>
              )}
            </div>

            <div className="d-flex" style={{ gap: '8px' }}>
              {isAnalyzing ? (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={onClose}
                    title="Close review modal and let analysis finish in background"
                  >
                    <i className="fas fa-compress-alt mr-1" /> Run in Background
                  </button>
                  {onCancelAndDiscard && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={onCancelAndDiscard}
                      title="Cancel Smart Upload and discard staged files"
                    >
                      <i className="fas fa-trash-alt mr-1" /> Cancel & Discard
                    </button>
                  )}
                </>
              ) : (
                (onCancelAndDiscard || onClose) && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={onCancelAndDiscard || onClose}
                    disabled={isApplying}
                    title="Cancel Smart Upload and discard files"
                  >
                    <i className="fas fa-trash-alt mr-1" /> Cancel & Discard
                  </button>
                )
              )}
              <button
                type="button"
                className="btn btn-sm btn-primary font-weight-bold px-3 shadow-sm"
                onClick={() => onApproveAndApply({ logRecordReviewTime, recordReviewHours })}
                disabled={isApplying || isAnalyzing || acceptedCount + mergedCount === 0 || (user && !canApply)}
                title={
                  isAnalyzing
                    ? 'Analysis in progress...'
                    : user && !canApply
                    ? 'You do not have permission to commit case changes.'
                    : acceptedCount + mergedCount === 0
                    ? 'Accept at least one entity before applying to case.'
                    : 'Commit accepted entities to the case record'
                }
              >
                {isApplying ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true" />
                    Applying Confirmed…
                  </>
                ) : (
                  <>
                    <i className="fas fa-check-double mr-1" />
                    Apply Confirmed to Case
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

HitlReviewDrawer.propTypes = {
  title: PropTypes.string,
  documentSubtype: PropTypes.string,
  entities: PropTypes.arrayOf(PropTypes.object).isRequired,
  activeEntityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelectEntity: PropTypes.func.isRequired,
  onAcceptEntity: PropTypes.func.isRequired,
  onRejectEntity: PropTypes.func.isRequired,
  onMergeEntity: PropTypes.func,
  onAssociateOrganization: PropTypes.func,
  onEditEntity: PropTypes.func.isRequired,
  onUndoEntity: PropTypes.func.isRequired,
  onUpdateMedicationFlags: PropTypes.func,
  onAcceptAllInCategory: PropTypes.func,
  onApproveAndApply: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  onCancelAndDiscard: PropTypes.func,
  availableOrganizations: PropTypes.arrayOf(PropTypes.string),
  user: PropTypes.object,
  isApplying: PropTypes.bool,
  isAnalyzing: PropTypes.bool,
  readOnly: PropTypes.bool,
}
